import { html, nothing } from "lit";
import type { FleetComposerMode } from "./fleet-composer-mode.ts";
import { parseAgentSessionKey } from "../../../../src/sessions/session-key-utils.js";
import {
  parseMessageForSubAgent,
  shouldSendToMainSessionInsteadOfSpawn,
  type AgentRef,
} from "./delegate-router.ts";
import { applyFleetSendOptionsToMessage } from "./fleet-chat-send-options.ts";
import { resolveComposerModeSendOptions } from "./fleet-composer-mode.ts";

export type FleetSendRoutingTone =
  | "main"
  | "delegate"
  | "orchestrate"
  | "project"
  | "self-improvement"
  | "mode"
  | "connect";

export type FleetSendRoutingPreview = {
  tone: FleetSendRoutingTone;
  headline: string;
  detail?: string;
};

export type FleetSendRoutingPreviewParams = {
  draft: string;
  sessionKey: string;
  connected: boolean;
  composerMode: FleetComposerMode;
  orchestrateWithAll: boolean;
  createNewProject: boolean;
  selfImprovement: boolean;
  sendProjectId: string | null;
  projects: Array<{ id: string; name: string }>;
  agents: AgentRef[];
};

function sessionHeadline(sessionKey: string): string {
  const parsed = parseAgentSessionKey(sessionKey.trim());
  if (!parsed) {
    return "this session";
  }
  if (parsed.rest === "main") {
    return parsed.agentId === "main" ? "Fleet Control (main)" : `${parsed.agentId} · main`;
  }
  return `${parsed.agentId} · ${parsed.rest}`;
}

function agentLabel(agents: AgentRef[], agentId: string, agentName?: string): string {
  if (agentName?.trim()) {
    return agentName.trim();
  }
  const hit = agents.find((a) => a.id === agentId);
  return hit?.name?.trim() || agentId;
}

/** What will happen when the user sends the current draft (mirrors handleSendChat routing). */
export function previewFleetSendRouting(
  params: FleetSendRoutingPreviewParams,
): FleetSendRoutingPreview | null {
  const draft = params.draft.trim();
  if (!draft) {
    return null;
  }

  if (!params.connected) {
    return {
      tone: "connect",
      headline: "Connect to the gateway to send",
      detail: "Your message is saved in the box until you are online.",
    };
  }

  const modeOverrides = resolveComposerModeSendOptions(params.composerMode);
  const orchestrate = Boolean(modeOverrides.orchestrateWithAll ?? params.orchestrateWithAll);
  const createNewProject = Boolean(modeOverrides.createNewProject ?? params.createNewProject);
  const selfImprovement = Boolean(modeOverrides.selfImprovement ?? params.selfImprovement);

  if (params.composerMode === "multitask") {
    return {
      tone: "orchestrate",
      headline: "Multitask mode — fans out with @all",
      detail:
        "Parallel workers on every configured agent. Switch to Agent mode for a single-thread reply.",
    };
  }

  if (params.composerMode !== "agent") {
    const modeLabel =
      params.composerMode === "ask"
        ? "Ask"
        : params.composerMode === "plan"
          ? "Plan"
          : params.composerMode === "debug"
            ? "Debug"
            : "Composer";
    return {
      tone: "mode",
      headline: `${modeLabel} mode — one agent on ${sessionHeadline(params.sessionKey)}`,
      detail:
        params.composerMode === "ask"
          ? "Read-only Q&A on this session (no file edits)."
          : params.composerMode === "plan"
            ? "Research and planning on this session; use Implement when ready to code."
            : "Focused debugging on this session.",
    };
  }

  if (selfImprovement) {
    return {
      tone: "self-improvement",
      headline: "Self improvement — code-fix worker + YouTrack",
      detail:
        "Creates a tracked task and runs the feature-ship pipeline (not a casual chat reply).",
    };
  }

  if (createNewProject) {
    return {
      tone: "project",
      headline: "Creates a new project, then sends there",
      detail: "A forked chat session and file root under .openclaw/fleet-projects/.",
    };
  }

  const projectId = params.sendProjectId?.trim() ?? "";
  if (projectId) {
    const project = params.projects.find((p) => p.id === projectId);
    const name = project?.name?.trim() || "selected project";
    return {
      tone: "project",
      headline: `Sends into project “${name}”`,
      detail: "Opens that project's session for this task.",
    };
  }

  const outbound = applyFleetSendOptionsToMessage(draft, {
    orchestrateWithAll: orchestrate,
    createNewProject: false,
    selfImprovement: false,
  });
  const manual = parseMessageForSubAgent(outbound, params.agents);

  if (orchestrate && manual.kind === "none") {
    return {
      tone: "orchestrate",
      headline: "Orchestrate with @all — every agent gets the task",
      detail: "Your message will be prefixed with @all when you send.",
    };
  }

  if (manual.kind === "all") {
    return {
      tone: "orchestrate",
      headline: "Fan-out to all agents (@all)",
      detail: "Spawns parallel workers — follow Subagent activity and Fleet map.",
    };
  }

  if (manual.kind === "active") {
    return {
      tone: "orchestrate",
      headline: "Message every active worker (@active)",
      detail: "Only agents with a running or queued subagent receive the task.",
    };
  }

  if (manual.kind === "best-of-n") {
    return {
      tone: "orchestrate",
      headline: `Best-of-${manual.n} — compare ${manual.n} runs`,
      detail: "Spawns multiple attempts so you can pick the best outcome.",
    };
  }

  if (manual.kind === "spawn") {
    if (shouldSendToMainSessionInsteadOfSpawn(params.sessionKey, manual.agentId)) {
      return {
        tone: "main",
        headline: `@${manual.agentId} on ${sessionHeadline(params.sessionKey)} — stays on this thread`,
        detail:
          "You are already on this agent's main session, so we chat here instead of spawning a worker.",
      };
    }
    const name = agentLabel(params.agents, manual.agentId, manual.agentName);
    return {
      tone: "delegate",
      headline: `Delegate to ${name} (@${manual.agentId})`,
      detail: "Spawns a subagent worker — you'll land on Fleet map after send.",
    };
  }

  return {
    tone: "main",
    headline: `Talk on ${sessionHeadline(params.sessionKey)}`,
    detail:
      "Plain text goes to this session via chat.send. Use @agent, Orchestrate, or a project only when you want workers.",
  };
}

export function formatFleetDelegationAck(outbound: string, agents: AgentRef[]): string {
  const manual = parseMessageForSubAgent(outbound.trim(), agents);
  switch (manual.kind) {
    case "all":
      return "Orchestration started — every agent has a worker. Check Fleet map and Subagent activity.";
    case "active":
      return "Sent to every active worker — follow progress on Fleet map.";
    case "best-of-n":
      return `Best-of-${manual.n} run started — compare outcomes on Fleet map.`;
    case "spawn": {
      const name = agentLabel(agents, manual.agentId, manual.agentName);
      return `Delegated to ${name} — follow the worker on Fleet map.`;
    }
    default:
      return "Workers spawned — follow progress on Fleet map.";
  }
}

export function renderFleetSendRoutingPreview(preview: FleetSendRoutingPreview | null) {
  if (!preview) {
    return nothing;
  }
  return html`
    <div
      class="chat-compose__routing-preview chat-compose__routing-preview--${preview.tone}"
      role="status"
      aria-live="polite"
    >
      <span class="chat-compose__routing-preview-headline">${preview.headline}</span>
      ${
        preview.detail
          ? html`
            <span class="chat-compose__routing-preview-detail">${preview.detail}</span>
          `
          : nothing
      }
    </div>
  `;
}

export function renderFleetSendAckBanner(ack: string | null | undefined, onDismiss?: () => void) {
  if (!ack?.trim()) {
    return nothing;
  }
  return html`
    <div class="chat-compose__send-ack callout success" role="status" aria-live="polite">
      <p class="chat-compose__send-ack-text">${ack}</p>
      ${
        onDismiss
          ? html`
            <button
              type="button"
              class="btn subtle btn--sm"
              title="Dismiss this confirmation"
              @click=${onDismiss}
            >
              Got it
            </button>
          `
          : nothing
      }
    </div>
  `;
}
